import { forwardRef, useId } from 'react'
import type { SVGProps } from 'react'

import EventManagement from '@/assets/icons/event-management.svg?react'
import EventOwner from '@/assets/icons/event-owner.svg?react'
import EventReport from '@/assets/icons/event-report.svg?react'
import Registration from '@/assets/icons/registration.svg?react'
import Delete from '@/assets/icons/delete.svg?react'
import Edit from '@/assets/icons/edit.svg?react'
import Meta from '@/assets/icons/meta.svg?react'
import User from '@/assets/icons/user.svg?react'
import DragHandle from '@/assets/icons/drag-handle.svg?react'

const ICONS = {
  eventManagement: EventManagement,
  eventOwner: EventOwner,
  eventReport: EventReport,
  registration: Registration,
  delete: Delete,
  edit: Edit,
  meta: Meta,
  user: User,
  dragHandle: DragHandle,
} as const

export type IconName = keyof typeof ICONS
type BaseProps = Omit<SVGProps<SVGSVGElement>, 'width' | 'height' | 'color'>

interface IconProps extends BaseProps {
  name: IconName
  size?: number | string // e.g. 16 or '1em'
  title?: string
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ name, size = 16, title, className, ...rest }, ref) => {
    const Component = ICONS[name]
    const titleId = useId()
    const labelledProps = title
      ? { role: 'img' as const, 'aria-labelledby': titleId }
      : { 'aria-hidden': true as const }

    return (
      <Component
        ref={ref}
        width={size}
        height={size}
        className={className}
        // Make sure your source SVGs use fill="currentColor" (or stroke)
        {...labelledProps}
        {...rest}
      >
        {title ? <title id={titleId}>{title}</title> : null}
      </Component>
    )
  }
)

export const iconNames = Object.keys(ICONS) as IconName[]
